import { getIstDateParts } from "./ist.js";

export type DocumentType = "Invoice" | "Receipt";

/**
 * Invoice number 'NN/MMYYYY' (no prefix) — NN = per-(space×month) sequence,
 * MM = month, YYYY = full year. Displayed with a leading '#'.
 */
export function formatInvoiceNumber(seq: number, invoiceDate: Date, _spaceId: number): string {
  const parts = getIstDateParts(invoiceDate);
  const nn = String(seq).padStart(2, "0");
  const mm = parts.month;
  const yyyy = parts.year;
  return `${nn}/${mm}${yyyy}`;
}

/**
 * Display an ORDER-RECEIPT number as 'NN/MMYYYY' (zero-padded continuous serial +
 * IST issue month), e.g. serial 5 issued Jun 2026 → '05/062026'.
 *
 * The stored receipt number is a continuous integer (the shared receipt series, so
 * uniqueness + idempotency are preserved); this formatting is DISPLAY-ONLY. Values
 * that already contain '/' (legacy 'NN/MMYYYY') or are non-numeric pass through
 * unchanged, so re-formatting is idempotent and safe on mixed data.
 */
export function formatOrderReceiptDisplay(
  receiptNumber: string | number | null | undefined,
  issueDate: Date,
): string {
  if (receiptNumber == null || receiptNumber === "") return "";
  const s = String(receiptNumber).trim();
  if (s.includes("/")) return s; // already formatted / legacy NN/MMYYYY
  const n = Number(s);
  if (!Number.isFinite(n)) return s; // non-numeric — leave as-is
  return formatInvoiceNumber(n, issueDate, 0);
}

/**
 * Phase 3.1 D-18 — Document number with R-/I- prefix.
 *   formatDocumentNumber(1, May 9 2026, 'Invoice') === 'I-01/05-26'
 *   formatDocumentNumber(2, May 9 2026, 'Receipt') === 'R-02/05-26'
 *
 * Sequence is the unified counter (R + I) per (space × month). Caller supplies
 * `seq` from the COUNT(*) query in the same tx that inserts the row.
 */
export function formatDocumentNumber(
  seq: number,
  date: Date,
  documentType: DocumentType,
): string {
  const parts = getIstDateParts(date);
  const nn = String(seq).padStart(2, "0");
  const mm = parts.month;
  const yy = parts.year.slice(-2);
  const prefix = documentType === "Receipt" ? "R" : "I";
  return `${prefix}-${nn}/${mm}-${yy}`;
}
